/**
 * Client-side image compression before upload.
 * Handles large iPhone photos by compressing + downscaling.
 * Uses browser-image-compression with web worker for non-blocking processing.
 */

import imageCompression from 'browser-image-compression';

const SKIP_THRESHOLD_BYTES = 600_000; // 600 KB

const COMPRESSION_OPTIONS = {
  maxSizeMB: 2,
  maxWidthOrHeight: 1600,
  useWebWorker: true,
  fileType: 'image/jpeg' as const,
  initialQuality: 0.8,
};

/**
 * Compress an image file if it exceeds the skip threshold.
 * Falls back to the original file on error.
 */
export async function compressImageFile(file: File): Promise<File> {
  // Skip non-images
  if (!file.type.startsWith('image/')) return file;

  // Skip small files
  if (file.size < SKIP_THRESHOLD_BYTES) {
    console.info('[compress] skipped (small)', file.name, `${(file.size / 1024).toFixed(0)} KB`);
    return file;
  }

  try {
    const compressed = await imageCompression(file, COMPRESSION_OPTIONS);

    console.info(
      '[compress]',
      file.name,
      `${(file.size / 1024 / 1024).toFixed(1)} MB → ${(compressed.size / 1024 / 1024).toFixed(1)} MB`,
    );

    // Return as a proper File (imageCompression returns a Blob-like File)
    return new File([compressed], file.name, { type: compressed.type });
  } catch (err) {
    console.error('[compress] failed, using original', file.name, err);
    return file;
  }
}
